import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import { useNavigate } from "react-router-dom";
import GlassCard from "@/components/GlassCard";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Flame, Play, Moon, Utensils, TrendingUp, ChevronRight, Share2 } from "lucide-react";

function ScoreCircle({ score, size = 120 }) {
  const radius = (size - 12) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;

  return (
    <div className="relative score-circle" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="6" />
        <circle
          cx={size/2} cy={size/2} r={radius} fill="none" stroke="#00f260" strokeWidth="6"
          strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={offset}
          className="transition-all duration-1000 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-heading text-3xl font-bold text-white">{score}</span>
        <span className="text-[10px] text-gray-400 uppercase tracking-wider">Longevity</span>
      </div>
    </div>
  );
}

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return "Jó reggelt";
  if (h < 18) return "Jó napot";
  return "Jó estét";
}

function getIntensityClass(intensity) {
  if (!intensity) return "intensity-low";
  const i = intensity.toLowerCase();
  if (i === "magas" || i === "high") return "intensity-high";
  if (i === "közepes" || i === "medium") return "intensity-medium";
  return "intensity-low";
}

export default function TodayPage() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api().get("/dashboard").then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [api]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-[#00f260] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const m = data?.metrics;
  const w = data?.today_workout;
  const r = data?.recovery;

  const caloriesConsumed = m?.calories_consumed || 0;
  const caloriesTarget = m?.calories_target || 2800;
  const caloriesPct = Math.min(100, Math.round((caloriesConsumed / caloriesTarget) * 100));

  return (
    <div className="space-y-6 md:pt-20" data-testid="today-page">
      {/* Header */}
      <div className="flex items-center justify-between fade-in-up">
        <div>
          <p className="text-gray-400 text-sm">{getGreeting()},</p>
          <h1 className="font-heading text-3xl font-bold text-white tracking-tight">
            {user?.name || "Atléta"}
          </h1>
        </div>
        <div onClick={() => navigate("/longevity")} className="cursor-pointer">
          <ScoreCircle score={m?.longevity_score || 87} size={80} />
        </div>
      </div>

      {/* Today's Workout */}
      <GlassCard testId="today-workout-card" glow className="fade-in-up fade-in-up-1" onClick={() => navigate("/workout")}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Mai edzés</p>
            <h3 className="font-heading text-xl font-semibold text-white">
              {w?.name || "Pihenőnap"}
            </h3>
          </div>
          <Badge className={`${getIntensityClass(w?.intensity)} border-0 text-xs`}>
            {w?.intensity || "---"}
          </Badge>
        </div>
        {w && (
          <div className="flex items-center gap-4 mb-4">
            <span className="text-sm text-gray-400">{w.duration_min} perc</span>
            <span className="text-sm text-gray-400">{w.exercises?.length || 0} gyakorlat</span>
          </div>
        )}
        <button
          data-testid="start-workout-btn"
          className="neon-btn text-xs py-2 px-5 flex items-center gap-2"
          onClick={(e) => { e.stopPropagation(); navigate("/workout"); }}
        >
          <Play size={14} /> Edzés indítása
        </button>
      </GlassCard>

      {/* Grid: Recovery + Nutrition */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Recovery Status */}
        <GlassCard testId="recovery-status-card" className="fade-in-up fade-in-up-2" onClick={() => navigate("/recovery")}>
          <div className="flex items-center gap-2 mb-4">
            <Moon size={16} className="text-[#00f260]" />
            <p className="text-xs text-gray-400 uppercase tracking-wider">Regeneráció</p>
          </div>
          <div className="space-y-3">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-400">Alvás pontszám</span>
                <span className="text-white font-medium">{r?.sleep_quality || 85}%</span>
              </div>
              <Progress value={r?.sleep_quality || 85} className="h-2 bg-white/5" />
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-400">HRV</span>
                <span className="text-white font-medium">{r?.hrv || 72} ms</span>
              </div>
              <Progress value={Math.min(100, ((r?.hrv || 72) / 100) * 100)} className="h-2 bg-white/5" />
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-400">Fáradtság</span>
                <span className="text-white font-medium">{r?.fatigue_level || 35}%</span>
              </div>
              <Progress value={r?.fatigue_level || 35} className="h-2 bg-white/5" />
            </div>
          </div>
        </GlassCard>

        {/* Nutrition Summary */}
        <GlassCard testId="nutrition-summary-card" className="fade-in-up fade-in-up-3" onClick={() => navigate("/nutrition")}>
          <div className="flex items-center gap-2 mb-4">
            <Utensils size={16} className="text-[#00f260]" />
            <p className="text-xs text-gray-400 uppercase tracking-wider">Táplálkozás</p>
          </div>
          <div className="mb-4">
            <div className="flex justify-between items-end mb-1">
              <span className="font-heading text-2xl font-bold text-white">{caloriesConsumed}</span>
              <span className="text-sm text-gray-400">/ {caloriesTarget} kcal</span>
            </div>
            <Progress value={caloriesPct} className="h-2 bg-white/5" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <MacroBar label="Fehérje" value={m?.protein || 0} target={m?.protein_target || 196} color="#00f260" />
            <MacroBar label="Szénhidrát" value={m?.carbs || 0} target={m?.carbs_target || 280} color="#ffc107" />
            <MacroBar label="Zsír" value={m?.fat || 0} target={m?.fat_target || 78} color="#ff5252" />
          </div>
        </GlassCard>
      </div>

      {/* Streak Card */}
      <GlassCard testId="streak-card" className="fade-in-up fade-in-up-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-[#00f260]/10 flex items-center justify-center flame-flicker">
              <Flame size={24} className="text-[#00f260]" />
            </div>
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wider">Aktív sorozat</p>
              <p className="font-heading text-2xl font-bold text-white">{m?.active_streak || 12} nap</p>
            </div>
          </div>
          <div className="flex items-center gap-1 text-[#00f260] text-sm">
            <TrendingUp size={16} />
            <span>+3 hét</span>
          </div>
        </div>
      </GlassCard>

      {/* Share Card */}
      <GlassCard testId="share-quick-card" className="fade-in-up fade-in-up-5" glow>
        <div className="flex items-center justify-between cursor-pointer" onClick={() => navigate("/share")}>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-[#00f260]/10 flex items-center justify-center">
              <Share2 size={22} className="text-[#00f260]" />
            </div>
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wider">Teljesítmény Megosztás</p>
              <p className="text-sm text-white">Oszd meg eredményeid a világgal</p>
            </div>
          </div>
          <ChevronRight size={20} className="text-gray-500" />
        </div>
      </GlassCard>

      {/* Quick Navigation */}
      <GlassCard testId="longevity-quick-card" className="fade-in-up fade-in-up-5" onClick={() => navigate("/longevity")}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <ScoreCircle score={m?.longevity_score || 87} size={56} />
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wider">Longevity Analitika</p>
              <p className="text-sm text-white">Biológiai kor: <span className="text-[#00f260] font-medium">{m?.biological_age?.toFixed(1) || "29.4"} év</span></p>
            </div>
          </div>
          <ChevronRight size={20} className="text-gray-500" />
        </div>
      </GlassCard>
    </div>
  );
}

function MacroBar({ label, value, target, color }) {
  const pct = Math.min(100, Math.round((value / target) * 100));
  return (
    <div className="text-center">
      <div className="h-16 w-full bg-white/5 rounded-lg relative overflow-hidden mb-1">
        <div
          className="absolute bottom-0 left-0 right-0 rounded-lg transition-all duration-700"
          style={{ height: `${pct}%`, backgroundColor: color, opacity: 0.6 }}
        />
      </div>
      <p className="text-[10px] text-gray-400">{label}</p>
      <p className="text-xs text-white font-medium">{value}g</p>
    </div>
  );
}
